import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableRow,
} from "@/components/ui/table"
import { useCopyToClipboard } from "usehooks-ts"
import CopyIcon from "@/components/icons/copy"
import { toast } from "sonner"
import { format } from "date-fns"
import { WalletTransactionType, useTransactions } from "@/api/transactions"
import { DropdownMenuItem } from "@/components/ui/dropdown-menu"
import { TokenIcon } from "@/components/token"
import Loader from "@/components/layout/loader"
import OptionsDropdownMenu from "@/components/ux/options.dropdown-menu"
import { Link } from "@/components/ui/link"
import { formatInvoiceNumber } from "@/api/invoices"
import LinkCircleIcon from "@/components/icons/link-circle"
import PaperFoldTextIcon from "@/components/icons/paper-fold-text"
import { useNavigate } from "react-router-dom"
import NetworkBadge from "@/components/ux/network.badge"
import NoteTextLineIcon from "@/components/icons/note-text-line"
import PaymentTypeIcon from "@/components/ux/payment-type.icon"

interface PaymentsTableProps {
  infinite?: boolean;
  paymentLinkId?: string;
}

const TABLE_COLUMNS = 6

export default function PaymentsTable({
  infinite = false,
  paymentLinkId,
}: PaymentsTableProps) {
  const { transactions, size, setSize, hasMore } = useTransactions({
    type: [
      WalletTransactionType.PAYMENT_RECEIVED,
      WalletTransactionType.PAYMENT_SENT,
    ],
    paymentLinkId,
    onlyPayments: !paymentLinkId ? true : undefined,
  })
  const [, copy] = useCopyToClipboard()
  const navigate = useNavigate()

  const handleCopy = async (hash: string) => {
    await copy(hash)
    toast.success("Copied transaction ID to clipboard")
  }

  if (!transactions) {
    return <Loader message="Loading payments..." />
  }

  return (
    <Table>
      <TableBody>
        {transactions.map((walletTransaction) => (
          <TableRow key={walletTransaction.id}>
            <TableCell className="px-4">
              <PaymentTypeIcon type={walletTransaction.type} />
            </TableCell>
            <TableCell>
              {format(
                new Date(walletTransaction.payment!.createdAt),
                "MMM do, yyyy",
              )}
            </TableCell>
            <TableCell>
              <div className="flex flex-row gap-2 items-center">
                {walletTransaction.transaction.token && (
                  <TokenIcon name={walletTransaction.transaction.token} />
                )}
                <div>
                  {walletTransaction.transaction.amount}{" "}
                  {walletTransaction.transaction.token}
                </div>
              </div>
            </TableCell>
            {!paymentLinkId && (
              <TableCell className="sm:table-cell">
                {walletTransaction.type ===
                  WalletTransactionType.PAYMENT_RECEIVED && (
                  <div>
                    {walletTransaction.payment &&
                      walletTransaction.payment.paymentLink && (
                        <Link
                          to={`/payment-links/${walletTransaction.payment.paymentLink.id}`}
                        >
                          <LinkCircleIcon />
                          {walletTransaction.payment.paymentLink.title}
                        </Link>
                      )}
                    {walletTransaction.payment &&
                      walletTransaction.payment.invoice && (
                        <Link
                          to={`/invoices/${walletTransaction.payment.invoice.id}`}
                        >
                          <PaperFoldTextIcon />#
                          {formatInvoiceNumber(
                            walletTransaction.payment.invoice.number,
                          )}
                        </Link>
                      )}
                  </div>
                )}
                {walletTransaction.type ===
                  WalletTransactionType.PAYMENT_SENT && (
                  <div className="flex gap-2">
                    {walletTransaction.payment &&
                      walletTransaction.payment.paymentLink && (
                        <>
                          <LinkCircleIcon />
                          {walletTransaction.payment.paymentLink.title}
                        </>
                      )}
                    {walletTransaction.payment &&
                      walletTransaction.payment.invoice && (
                        <>
                          <PaperFoldTextIcon />#
                          {formatInvoiceNumber(
                            walletTransaction.payment.invoice.number,
                          )}
                        </>
                      )}
                  </div>
                )}
              </TableCell>
            )}
            <TableCell className="hidden sm:table-cell">
              {walletTransaction.type ===
                WalletTransactionType.PAYMENT_RECEIVED && (
                <div>
                  {walletTransaction.payment &&
                    walletTransaction.payment.invoice && (
                      <Link
                        to={`/customers/${walletTransaction.payment.invoice.customer.id}`}
                      >
                        {walletTransaction.payment.invoice.customer.firstName}{" "}
                        {walletTransaction.payment.invoice.customer.lastName}
                      </Link>
                    )}
                  {walletTransaction.payment &&
                    walletTransaction.payment.paymentLink && (
                      <div>{walletTransaction.payment.name}</div>
                    )}
                </div>
              )}
              {walletTransaction.type ===
                WalletTransactionType.PAYMENT_SENT && (
                <div>
                  {walletTransaction.payment?.wallet.user.firstName}{" "}
                  {walletTransaction.payment?.wallet.user.lastName}
                </div>
              )}
            </TableCell>
            <TableCell className="hidden sm:table-cell">
              <NetworkBadge network={walletTransaction.transaction.network} />
            </TableCell>
            <TableCell>
              <OptionsDropdownMenu>
                <DropdownMenuItem
                  onClick={() => navigate(`/payments/${walletTransaction.id}`)}
                >
                  <NoteTextLineIcon />
                  Details
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleCopy(walletTransaction.transaction.hash)}
                >
                  <CopyIcon />
                  Transaction ID
                </DropdownMenuItem>
              </OptionsDropdownMenu>
            </TableCell>
          </TableRow>
        ))}
        {transactions.length === 0 && (
          <TableRow>
            <TableCell
              colSpan={TABLE_COLUMNS}
              className="text-center text-gray-300"
            >
              No transactions found
            </TableCell>
          </TableRow>
        )}
      </TableBody>
      {infinite && hasMore && (
        <TableFooter>
          <TableRow>
            <TableCell colSpan={TABLE_COLUMNS}>
              <button
                onClick={() => setSize(size + 1)}
                className="flex flex-col w-full items-center bg-white p-3 text-center"
              >
                Load more...
              </button>
            </TableCell>
          </TableRow>
        </TableFooter>
      )}
    </Table>
  )
}
